import {
  type BestAvailableSeatingQueryParamsActionType,
  type BestAvailableSeatingQueryParamsType,
} from "@shared/modules/seating/types/seating-types";
import isEmpty from "lodash/isEmpty";
import { useReducer } from "react";

export const DEFAULT_BEST_AVAILABLE_RESULT_LIMIT = 25;

export enum BestAvailableQueryActions {
  EVENT,
  QUANTITY,
  PACKAGE,
  TICKET_TYPES,
  IS_ACCESSIBLE,
  MEMBERSHIP_GROUP,
  MEMBERSHIP_LEVELS,
  MEMBERSHIP,
  RESULT_LIMIT,
  SET_SEGMENT_ID,
  ALL_PERMISSIONS,
  MEMBERSHIP_BENEFIT,
  RESET,
}

const defaultValues: BestAvailableSeatingQueryParamsType = {
  event: undefined,
  quantity: 0,
  package: undefined,
  ticket_types: undefined,
  is_accessible: undefined,
  membership_group: undefined,
  membership_levels: undefined,
  result_limit: DEFAULT_BEST_AVAILABLE_RESULT_LIMIT,
  segment_id: undefined,
};

export const bestAvailableSeatQueryReducer = (
  prevState: BestAvailableSeatingQueryParamsType,
  action: BestAvailableSeatingQueryParamsActionType
): BestAvailableSeatingQueryParamsType => {
  const { type, payload } = action;

  switch (type) {
    case BestAvailableQueryActions.EVENT:
      return {
        ...prevState,
        event: payload.event,
        membership_group: undefined,
        package: undefined,
      };
    case BestAvailableQueryActions.QUANTITY: {
      /**
       * When there is only one ticket type, we dont use the multi ticket type select flow.
       * So we need to update the quantity for the single ticket type to match the quantity selected.
       */
      if (prevState.ticket_types) {
        if (Object.keys(prevState.ticket_types).length === 1) {
          const ttKey = Object.keys(prevState.ticket_types)[0];
          return {
            ...prevState,
            ticket_types: { [ttKey]: payload.quantity },
            quantity: payload.quantity,
          };
        }
      }
      /**
       * When there is only one membership level, we dont use the multi membership level select flow.
       * So we need to update the quantity for the single membership level to match the quantity selected.
       */
      if (prevState.membership_levels) {
        if (Object.keys(prevState.membership_levels).length === 1) {
          const mlKey = Object.keys(prevState.membership_levels)[0];
          return {
            ...prevState,
            membership_levels: { [mlKey]: payload.quantity },
            quantity: payload.quantity,
          };
        }
      }
      return { ...prevState, quantity: payload.quantity };
    }
    case BestAvailableQueryActions.PACKAGE:
      return {
        ...prevState,
        package: payload.package,
        membership_group: undefined,
        event: undefined,
      };
    case BestAvailableQueryActions.TICKET_TYPES:
      return {
        ...prevState,
        membership_levels: undefined,
        ticket_types: isEmpty(payload.ticket_types)
          ? undefined
          : payload.ticket_types,
        quantity: Object.values(payload.ticket_types || {}).reduce(
          (acc: number, curr: number) => acc + curr,
          0
        ) as number,
      };
    case BestAvailableQueryActions.IS_ACCESSIBLE:
      return { ...prevState, is_accessible: payload.is_accessible };
    case BestAvailableQueryActions.MEMBERSHIP_GROUP:
      return {
        ...prevState,
        membership_group: payload.membership_group,
        event: undefined,
        package: undefined,
      };
    case BestAvailableQueryActions.MEMBERSHIP_LEVELS:
      return {
        ...prevState,
        ticket_types: undefined,
        membership_levels: isEmpty(payload.membership_levels)
          ? undefined
          : payload.membership_levels,
        quantity: Object.values(payload.membership_levels || {}).reduce(
          (acc: number, curr: number) => acc + curr,
          0
        ),
      };
    case BestAvailableQueryActions.RESULT_LIMIT:
      return { ...prevState, result_limit: payload.result_limit };
    case BestAvailableQueryActions.SET_SEGMENT_ID:
      return { ...prevState, segment_id: payload.segment_id };
    case BestAvailableQueryActions.ALL_PERMISSIONS:
      return { ...prevState, allPermissions: payload.allPermissions };
    case BestAvailableQueryActions.MEMBERSHIP_BENEFIT:
      return { ...prevState, membership_benefit: payload.membership_benefit };
    case BestAvailableQueryActions.RESET:
      return defaultValues;
    default:
      return prevState;
  }
};

export default function useBestAvailableQueryParams() {
  const [bestAvailableSeatingQueryParams, dispatch] = useReducer(
    bestAvailableSeatQueryReducer,
    defaultValues,
    (initialState) => initialState
  );

  return {
    bestAvailableSeatingQueryParams,
    updateBestAvailableSeatingQueryParams: dispatch,
  };
}
